import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface Transaction {
  id: string;
  property_number: string;
  agency: string;
  negotiator: string;
  seller: string;
  buyer: string;
  sale_price: number;
  fees_with_tax: number;
  fees_without_tax: number;
  sale_date: string;
  status: 'pending' | 'validated' | 'modification_requested';
}

interface ModificationRequest {
  id: string;
  transaction_id: string;
  requested_by: string;
  status: 'pending' | 'approved' | 'rejected';
  changes_requested: Record<string, any>;
  created_at: string;
  user?: {
    firstname: string;
    lastname: string;
  };
}

interface ModificationRequestModalProps {
  transaction: Transaction | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function ModificationRequestModal({
  transaction,
  isOpen,
  onClose,
  onSuccess
}: ModificationRequestModalProps) {
  const { user } = useAuth();
  const [modificationRequests, setModificationRequests] = useState<ModificationRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newRequest, setNewRequest] = useState('');

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (transaction) {
      loadModificationRequests();
    }
  }, [transaction]);

  const loadModificationRequests = async () => {
    if (!transaction) return;

    try {
      const { data, error } = await supabase
        .from('modification_requests')
        .select(`
          *,
          user:requested_by (
            firstname,
            lastname
          )
        `)
        .eq('transaction_id', transaction.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setModificationRequests(data || []);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!transaction || !user || !newRequest.trim()) return;

    try {
      setLoading(true);
      
      // Créer la demande de modification
      const { error: requestError } = await supabase
        .from('modification_requests')
        .insert([{
          transaction_id: transaction.id,
          requested_by: user.id,
          status: 'pending',
          changes_requested: { message: newRequest.trim() }
        }]);

      if (requestError) throw requestError;

      // Mettre à jour le statut de la transaction
      const { error: transactionError } = await supabase
        .from('transactions')
        .update({ status: 'modification_requested' })
        .eq('id', transaction.id);

      if (transactionError) throw transactionError;

      setNewRequest('');
      await loadModificationRequests();
      onSuccess();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseRequest = async () => {
    if (!isAdmin || !transaction) return;

    try {
      setLoading(true);
      
      // Mettre à jour le statut de la transaction à "pending"
      const { error: transactionError } = await supabase
        .from('transactions')
        .update({ status: 'pending' })
        .eq('id', transaction.id);

      if (transactionError) throw transactionError;

      // Mettre à jour toutes les demandes en attente
      const { error: requestsError } = await supabase
        .from('modification_requests')
        .update({ status: 'approved' })
        .eq('transaction_id', transaction.id)
        .eq('status', 'pending');

      if (requestsError) throw requestsError;

      await loadModificationRequests();
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !transaction) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200]">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={20} />
        </button>

        <h2 className="text-xl font-bold mb-6">
          Demandes de modification - Transaction #{transaction.property_number}
        </h2>

        <div className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nouvelle demande de modification
              </label>
              <textarea
                value={newRequest}
                onChange={(e) => setNewRequest(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                rows={3}
                placeholder="Décrivez les modifications souhaitées..."
                required
              />
            </div>

            {error && (
              <div className="text-red-500 text-sm">
                {error}
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Envoi...' : 'Envoyer la demande'}
              </button>
            </div>
          </form>

          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Historique des demandes
            </h3>

            <div className="space-y-4">
              {modificationRequests.length === 0 ? (
                <p className="text-gray-500 text-sm">
                  Aucune demande de modification pour cette transaction.
                </p>
              ) : (
                modificationRequests.map((request) => (
                  <div
                    key={request.id}
                    className="bg-gray-50 rounded-lg p-4 space-y-2"
                  >
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">
                            {request.user?.firstname} {request.user?.lastname}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(request.created_at).toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <p className="text-sm text-gray-900">
                          {request.changes_requested.message}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        request.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : request.status === 'approved'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {request.status === 'pending' ? 'En attente' :
                         request.status === 'approved' ? 'Approuvée' : 'Rejetée'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Fermer
            </button>

            {isAdmin && (
              <button
                type="button"
                onClick={handleCloseRequest}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                {loading ? 'Clôture...' : 'Clôturer la demande'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}